import React, { useRef } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import moment from 'moment';
import { Moment } from 'moment/moment';
import { setSnackbar } from 'flow/actions/app-actions';
import BaseInput from 'flow/components/ui-components/BaseInput';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Button from 'flow/components/ui-components/Button';
import TimeInput from 'flow/components/ui-components/TimeInput';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';
import { resolveDefaultDuration } from 'flow/utils/profile-utils';

interface Props {
    profile?: TypedMap<Profile>;
    onSubmit?: (
        profile: TypedMap<Profile>,
        until: Moment,
        event?: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => void;
}

const ProfilePickerModal = ({ profile, onSubmit }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { closeModal } = useModal();

    const timeRef = useRef<HTMLInputElement>(null);
    const dateRef = useRef<HTMLInputElement>(null);

    const now = moment();
    const soon = moment().add(1, 'hour').minute(0);
    const tomorrow = moment().add(1, 'day').hour(8).minute(0);

    interface Option {
        label: string;
        duration: moment.DurationInputArg1;
    }

    const getLabel = (
        amount: number,
        unit: 'weeks' | 'days' | 'hours' | 'minutes',
        overwriteLabel?: string,
    ) => {
        const labels = {
            weeks: 'datetime.weeks',
            days: 'datetime.days',
            hours: 'datetime.hours',
            minutes: 'datetime.minutes',
        };

        return {
            label: overwriteLabel || i18next.t(labels[unit], { count: amount }),
            duration: { [unit]: amount },
        };
    };

    const quickOptions: Record<'today' | 'future', Option[]> = {
        today: [
            {
                label: i18next.t('profilechooser.until_further_notice'),
                duration: null,
            },
            getLabel(15, 'minutes'),
            getLabel(30, 'minutes'),
            getLabel(45, 'minutes'),
            getLabel(1, 'hours'),
            getLabel(2, 'hours'),
            getLabel(12, 'hours'),
            getLabel(1, 'days'),
        ],
        future: [
            {
                label: i18next.t('profilechooser.until_further_notice'),
                duration: null,
            },
            getLabel(1, 'days', i18next.t('datetime.tomorrow')),
            getLabel(2, 'days'),
            getLabel(3, 'days'),
            getLabel(4, 'days'),
            getLabel(5, 'days'),
            getLabel(1, 'weeks'),
            getLabel(2, 'weeks'),
        ],
    };

    const isFuture = resolveDefaultDuration(
        profile.get('defaultDuration'),
        moment(),
    )?.isAfter(new Date(), 'day');

    const handleSubmit = () => {
        const rightNow = moment().format(ISO_8601_MS_RESOLUTION);
        const dateTime = `${dateRef.current.value} ${timeRef.current.value}`;
        const formattedDateTime = moment(dateTime, 'YYYY-MM-DD HH:mm').format(
            ISO_8601_MS_RESOLUTION,
        );

        if (rightNow < formattedDateTime) {
            onSubmit(profile, moment(dateTime, 'YYYY-MM-DD HH:mm'));
        } else {
            dispatch(
                setSnackbar({
                    i18nextMessage: 'snackbar.error',
                }),
            );
        }
    };

    const handleClick = (option: Option, isFuture: boolean) => {
        const endTime = !option.duration
            ? null //Until further notice
            : moment().add(option.duration);

        onSubmit(
            profile,
            isFuture && endTime ? endTime.hour(8).minute(0).second(0) : endTime,
        );
        closeModal();
    };

    return (
        <BaseModal
            open={true}
            header={profile.get('description')}
            onSubmit={handleSubmit}
        >
            <div className="flex flex-col gap-6 divide-y">
                <div className="grid grid-cols-2 gap-3 pb-6">
                    {quickOptions[isFuture ? 'future' : 'today'].map(
                        (option) => (
                            <Button
                                color="gray"
                                key={option.label}
                                className="hover:bg-main hover:darken-0 w-full hover:text-white"
                                onClick={() => handleClick(option, isFuture)}
                            >
                                {option.label}
                            </Button>
                        ),
                    )}
                </div>

                <div className="flex flex-col gap-3">
                    <p className="w-full text-start text-sm font-bold">
                        {i18next.t('profilechooser.set.profile_until')}
                    </p>

                    <TimeInput
                        ref={timeRef}
                        defaultValue={(isFuture ? tomorrow : soon).format(
                            'HH:mm',
                        )}
                    />
                    <BaseInput
                        type="date"
                        ref={dateRef}
                        defaultValue={(isFuture ? tomorrow : soon).format(
                            'YYYY-MM-DD',
                        )}
                        min={now.format('YYYY-MM-DD')}
                    />
                </div>
            </div>
        </BaseModal>
    );
};

export default ProfilePickerModal;
